import { Link } from '@tanstack/react-router';
import { useCFAuth } from '@/lib/auth/cf-auth-context';
import { buildGettingStartedPath } from '@/lib/cloudflare/billing-client';
import { APP_VERSION } from '@/lib/version';
import { IconMoon, IconSun, IconSettings, IconSearch, IconX, IconRefresh, IconRotate } from "@tabler/icons-react";
import { cn } from '@/lib/utils';
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarTrigger,
} from "@/components/ui/menubar";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useSyncStore } from '@/lib/store/sync.store';
import { usePreferencesStore } from '@/lib/store/preferences.store';
import { useAuthStore } from '@/lib/store/auth.store';
import { useSearchStore } from '@/lib/store/search.store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DynamicIsland } from '@/components/dynamic-island';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { AnimatePresence, motion } from 'motion/react';
import { toast } from "sonner";
import { useTriggerSync } from '@/hooks/useTriggerSync';
import { useState } from 'react';

type HeaderSyncActionDetail = {
  action: 'refresh';
};

export function Topbar() {
  const { user, status, signIn, signOut } = useCFAuth();
  const { theme, setTheme } = usePreferencesStore();
  const { globalSearch, setGlobalSearch, clearSearch } = useSearchStore();

  const {
    progress,
    isPolling,
    currentJobId,
    hasCheckedSyncState,
  } = useSyncStore();
  const isSyncing = hasCheckedSyncState && isPolling && progress?.status === 'processing' && Boolean(currentJobId || progress?.jobId);
  const showDynamicIsland = hasCheckedSyncState && isSyncing;

  const { triggerSync } = useTriggerSync();
  const [isReprocessConfirmOpen, setIsReprocessConfirmOpen] = useState(false);

  const toggleTheme = () => {
    if (theme === 'dark') {
      setTheme('light');
    } else {
      setTheme('dark');
    }
  };

  const handleLogin = () => {
    signIn(buildGettingStartedPath());
  };

  const handleLogout = async () => {
    // Clear stores
    usePreferencesStore.getState().reset();
    useAuthStore.getState().reset();
    // Sign out
    await signOut();
  };

  const triggerRefreshNow = () => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(
      new CustomEvent<HeaderSyncActionDetail>('winbox:sync-action', {
        detail: { action: 'refresh' },
      })
    );
    toast.success('Incremental refresh requested');
  };

  const handleResyncAll = async () => {
    const result = await triggerSync({
      maxEmails: 5000,
      onSuccess: () => {
        toast.success('Full re-sync started. Fresh analysis is running.');
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to start full re-sync');
      },
    });

    if (result.noEmails) {
      toast.info('No emails found to sync');
    }
  };

  const handleReprocessConfirm = async () => {
    setIsReprocessConfirmOpen(false);
    await handleResyncAll();
  };


  // Use compact header for logged-in users (also during loading to prevent height jump)
  const isCompact = status !== 'unauthenticated';

  return (
    <header className="fixed top-0 left-0 right-0 z-50 w-full bg-background border-b">
      <div className={cn(
        "w-full px-4 sm:px-6 lg:px-8 relative flex items-center",
        isCompact ? "h-11" : "h-16"
      )}>
        {/* Left section: logo */}
        <div className="flex items-center gap-2 shrink-0 z-10">
          <Link to="/" className="text-xl font-semibold flex items-center">
            <img
              src="/logo.svg"
              alt="Logo"
              className="h-8"
            />
          </Link>
          {!isCompact && (
            <span className="text-[10px] text-muted-foreground/50">
              {APP_VERSION}
            </span>
          )}
        </div>

        {/* Center: Search or Dynamic Island */}
        {(status === 'authenticated' || status === 'loading') && (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-xl px-2 sm:px-4 flex items-center justify-center pointer-events-none">
            <AnimatePresence mode="wait" initial={false}>
              {showDynamicIsland ? (
                <motion.div
                  key="dynamic-island"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="pointer-events-auto"
                >
                  <DynamicIsland />
                </motion.div>
              ) : (
                <motion.div
                  key="search-bar"
                  className="relative w-full pointer-events-auto"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <IconSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                  <Input
                    placeholder="Search emails..."
                    className="pl-9 h-8 text-sm rounded-xl"
                    value={globalSearch}
                    onChange={(e) => setGlobalSearch(e.target.value)}
                  />
                  {globalSearch && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                      onClick={clearSearch}
                    >
                      <IconX className="h-3 w-3" />
                    </Button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Right section: theme/user */}
        <nav className="ml-auto flex items-center gap-3 z-10">
          {/* Only show login button when explicitly unauthenticated (not during loading) */}
          {status === 'unauthenticated' && (
            <button
              onClick={handleLogin}
              className="flex items-center gap-2 text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 px-4 py-2 rounded-md transition-colors border border-slate-300 hover:border-slate-400"
            >
              <img
                src="/google.svg"
                alt="Google"
                width={18}
                height={18}
                className="w-[18px] h-[18px]"
              />
              Sign in with Google
            </button>
          )}

          <div className="flex items-center gap-1">
            {/* User Menu - only show when confirmed logged in */}
            {user && (
              <Menubar className="border-0 p-0 h-auto bg-transparent">
                <MenubarMenu>
                  <MenubarTrigger className="p-0 cursor-pointer h-auto data-[state=open]:bg-transparent focus:bg-transparent">
                    <Avatar className="h-7 w-7 sm:h-8 sm:w-8">
                      <AvatarImage src={user.image || ''} alt={user.name || ''} />
                      <AvatarFallback>
                        {user.name?.charAt(0).toUpperCase() || '?'}
                      </AvatarFallback>
                    </Avatar>
                  </MenubarTrigger>
            <MenubarContent align="end" className="w-[260px]">
              <div className="px-2 py-1.5 border-b border-border/20 mb-0.5">
                <div className="text-xs font-medium truncate">{user.name}</div>
                <div className="text-[11px] text-muted-foreground truncate">{user.email}</div>
              </div>
              <MenubarItem asChild>
                <Link to="/settings" search={{ section: 'labels' }} className="flex items-center gap-2 cursor-pointer">
                  <IconSettings className="h-3.5 w-3.5" />
                  Settings
                </Link>
              </MenubarItem>
              <MenubarSeparator />
              <MenubarItem onClick={triggerRefreshNow} className="flex items-center gap-2">
                <IconRefresh className="h-3.5 w-3.5" />
                Check for new mail
              </MenubarItem>
              <MenubarItem onClick={() => setIsReprocessConfirmOpen(true)} className="flex items-center gap-2">
                <IconRotate className="h-3.5 w-3.5" />
                Reprocess all mail
              </MenubarItem>
              <MenubarItem onClick={toggleTheme} className="flex items-center gap-2">
                {theme === 'dark' ? (
                  <>
                    <IconSun className="h-3.5 w-3.5" />
                    Theme
                  </>
                ) : (
                  <>
                    <IconMoon className="h-3.5 w-3.5" />
                    Theme
                  </>
                )}
              </MenubarItem>
              <MenubarSeparator />
              <MenubarItem onClick={handleLogout} className="text-red-600 cursor-pointer">
                Logout
              </MenubarItem>
            </MenubarContent>
                </MenubarMenu>
              </Menubar>
            )}
          </div>
        </nav>

        <AlertDialog open={isReprocessConfirmOpen} onOpenChange={setIsReprocessConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reprocess all mail?</AlertDialogTitle>
              <AlertDialogDescription>
                This will start a full mailbox re-sync and reprocess all emails.
                It can take a few minutes for large inboxes.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleReprocessConfirm}>Reprocess all</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </header>
  );
}
